/**
 * Public /verify page — the QR-code landing.
 *
 * An auditor scans the QR on page 1 of an assurance pack, lands here
 * with the fingerprint pre-filled in the URL (`?fp=<hash>&fw=<framework>`),
 * and gets an instant match / mismatch verdict without needing to log in
 * or type 64 hex characters.
 *
 * Backed by `POST /api/public/assurance-pack/verify` — the public-lite
 * variant that returns only `{match, framework, verified_at, message}`
 * (no `fingerprint_current`, no `controls_in_scope`) to avoid leaking
 * live tenant state to anonymous callers.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { PublicShell } from "@/components/PublicShell";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import { CheckCircle2, AlertTriangle, Loader2, ShieldCheck } from "lucide-react";

const HEX64 = /^[0-9a-f]{64}$/i;

export default function Verify() {
  const [params] = useSearchParams();
  const initialFp = (params.get("fp") || "").trim().toLowerCase();
  const initialFw = params.get("fw") || "";

  const [fingerprint, setFingerprint] = useState(initialFp);
  const [framework, setFramework] = useState(initialFw === "full" ? "" : initialFw);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (fp, fw) => {
    const normalised = (fp || "").trim().toLowerCase().replace(/\s+/g, "");
    if (!HEX64.test(normalised)) {
      setError("That doesn't look like a 64-character SHA-256 fingerprint. Copy the value under 'SOURCE-DATA FINGERPRINT (SHA-256)' on page 1 of the pack.");
      return;
    }
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/public/assurance-pack/verify", {
        fingerprint: normalised,
        framework: fw || null,
      });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Verification failed.");
    } finally {
      setBusy(false);
    }
  };

  // Auto-verify when the QR-code URL carries a valid fp — the user
  // never has to click. This is the entire point of the QR loop.
  //
  // Guard with a ref so React 18 StrictMode's dev-only double-invoke
  // doesn't double-consume the anon rate-limit + double-write the
  // audit row per page-load. Prod is unaffected either way.
  const autoFired = useRef(false);
  useEffect(() => {
    if (autoFired.current) return;
    if (initialFp && HEX64.test(initialFp)) {
      autoFired.current = true;
      submit(initialFp, initialFw === "full" ? "" : initialFw);
    }
  }, []);

  return (
    <PublicShell>
      <Seo title="Verify Assurance Pack" description="Verify the tamper-evident source-data fingerprint of an EKSELENS · ERIO assurance pack." path="/verify" />
      <section className="mx-auto max-w-2xl px-6 py-16" data-testid="verify-page">
        <p className="text-xs font-semibold uppercase tracking-widest text-ek-purple">Tamper-evident verification</p>
        <h1 className="mt-2 font-display text-3xl font-bold text-ek-ink">Verify an ERIO assurance pack</h1>
        <p className="mt-3 text-base text-ek-ink/70">
          Paste (or scan the QR from) any pack&rsquo;s <strong>source-data fingerprint</strong>.
          If the underlying assurance state hasn&rsquo;t moved, the fingerprint on the pack cover
          matches the current state byte-for-byte.
        </p>

        <form
          onSubmit={(e) => { e.preventDefault(); submit(fingerprint, framework); }}
          className="mt-8 grid gap-3"
          data-testid="verify-form"
        >
          <label className="text-xs font-semibold uppercase tracking-wide text-ek-ink/60">Fingerprint (64 hex chars)</label>
          <textarea
            required
            value={fingerprint}
            onChange={(e) => setFingerprint(e.target.value)}
            placeholder="e.g. fb187585d1671e6705e9d99a8e4f806c2bd7bd4e1cae369bd758d8010013b794"
            rows={2}
            spellCheck={false}
            className="rounded-md border border-black/15 px-3 py-2 font-mono text-xs"
            data-testid="verify-fingerprint"
          />

          <label className="mt-1 text-xs font-semibold uppercase tracking-wide text-ek-ink/60">Framework scope (optional)</label>
          <select
            value={framework}
            onChange={(e) => setFramework(e.target.value)}
            className="rounded-md border border-black/15 px-3 py-2 text-sm"
            data-testid="verify-framework"
          >
            <option value="">Full ERIO catalogue</option>
            <option value="soc2">SOC 2</option>
            <option value="iso27001">ISO 27001</option>
            <option value="dora">DORA</option>
            <option value="nis2">NIS2</option>
          </select>

          {error && <p className="text-sm text-rose-700" data-testid="verify-error">{error}</p>}

          <button
            type="submit"
            disabled={busy}
            className="ek-btn mt-2 inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold disabled:opacity-60"
            data-testid="verify-submit"
          >
            {busy ? <><Loader2 size={16} className="animate-spin" /> Verifying…</> : <><ShieldCheck size={16} /> Verify fingerprint</>}
          </button>
        </form>

        {result && (
          <div className="mt-8" data-testid="verify-result">
            {result.match ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-6" data-testid="verify-match">
                <div className="flex items-center gap-2 text-emerald-900">
                  <CheckCircle2 size={22} className="text-emerald-700" />
                  <h2 className="text-lg font-semibold">Match — assurance state unchanged</h2>
                </div>
                <p className="mt-3 text-sm text-emerald-900/85">{result.message}</p>
                <p className="mt-2 text-xs text-emerald-900/65">Scope: <strong>{result.framework === "full" ? "Full ERIO catalogue" : result.framework?.toUpperCase()}</strong> · Verified at {result.verified_at}</p>
              </div>
            ) : (
              <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-6" data-testid="verify-mismatch">
                <div className="flex items-center gap-2 text-amber-900">
                  <AlertTriangle size={22} className="text-amber-700" />
                  <h2 className="text-lg font-semibold">Mismatch — state has moved</h2>
                </div>
                <p className="mt-3 text-sm text-amber-900/85">{result.message}</p>
                <p className="mt-2 text-xs text-amber-900/65">Scope: <strong>{result.framework === "full" ? "Full ERIO catalogue" : result.framework?.toUpperCase()}</strong> · Verified at {result.verified_at}</p>
              </div>
            )}
          </div>
        )}

        <div className="mt-12 border-t border-black/10 pt-6 text-xs text-ek-ink/55">
          <p>
            Have questions about a specific pack? Contact the issuing tenant Owner, or{" "}
            <Link to="/trust" className="text-ek-purple hover:underline">learn how ERIO&rsquo;s tamper-evidence works</Link>.
          </p>
        </div>
      </section>
    </PublicShell>
  );
}
